import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RefreshCcw, MoreHorizontal, ShieldCheck, Clock, Ban, Trash2, Gift, Tag } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  rejected: "bg-red-100 text-red-800",
  revoked: "bg-zinc-200 text-zinc-700",
};

const STATUS_ICONS = {
  active: <ShieldCheck className="w-3.5 h-3.5" />,
  pending: <Clock className="w-3.5 h-3.5" />,
  rejected: <Ban className="w-3.5 h-3.5" />,
  revoked: <Ban className="w-3.5 h-3.5" />,
};

const TIER_COLORS = {
  trial: "bg-blue-100 text-blue-800",
  basic: "bg-slate-200 text-slate-800",
  pro: "bg-purple-100 text-purple-800",
  ultra: "bg-fuchsia-100 text-fuchsia-800",
  none: "bg-zinc-100 text-zinc-500",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [grantTarget, setGrantTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([
        api.get("/admin/users/detailed"),
        api.get("/admin/stats"),
      ]);
      setUsers(u.data.users || []);
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/users/${id}/status`, { status });
      toast.success(`Status set to ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Delete ${email}? This removes their linked Drive, TikTok accounts and logs.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const revokeComp = async (id, email) => {
    if (!window.confirm(`Revoke ${email}'s comp plan? They will drop to no plan immediately.`)) return;
    try {
      await api.post(`/admin/users/${id}/revoke-comp`);
      toast.success("Comp plan revoked");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const pending = users.filter((u) => u.status === "pending");
  const others = users.filter((u) => u.status !== "pending");

  const renderRow = (u) => {
    const status = u.status || "active";
    const tier = u.tier_active || "none";
    return (
      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
        <TableCell>
          <div className="text-sm font-medium text-slate-900">{u.name || "—"}</div>
          <div className="text-xs text-slate-500 break-all">{u.email}</div>
          {u.is_admin && <Badge className="mt-1 bg-blue-100 text-blue-800 hover:bg-blue-100 text-[10px]">Admin</Badge>}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <div className="flex items-center gap-1 flex-wrap">
            <Badge className={`${TIER_COLORS[tier] || TIER_COLORS.none} hover:${TIER_COLORS[tier] || TIER_COLORS.none} text-[10px] uppercase`} data-testid={`tier-${u.id}`}>
              {tier}
            </Badge>
            {u.is_comp && <Badge className="bg-fuchsia-100 text-fuchsia-800 hover:bg-fuchsia-100 text-[10px]" title={u.comp_reason || ""}>Comp</Badge>}
            {u.subscription_status === "canceling" && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Canceling</Badge>}
          </div>
          {u.today_videos > 0 || u.today_carousels > 0 ? (
            <div className="text-[11px] text-slate-500 tabular-nums mt-1">
              Today: {u.today_videos}v · {u.today_carousels}c
            </div>
          ) : null}
        </TableCell>
        <TableCell className="whitespace-nowrap">
          <Badge className={`${STATUS_COLORS[status] || "bg-zinc-100 text-zinc-700"} hover:${STATUS_COLORS[status]}`}>
            {STATUS_ICONS[status]} {status}
          </Badge>
        </TableCell>
        <TableCell className="text-xs text-slate-500 tabular-nums whitespace-nowrap">
          {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
        </TableCell>
        <TableCell className="text-right">
          <div className="hidden md:flex items-center gap-2 justify-end">
            {status !== "active" && (
              <Button size="sm" onClick={() => setStatus(u.id, "active")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${u.id}`}>
                Approve
              </Button>
            )}
            {status === "active" && (
              <Button size="sm" variant="outline" onClick={() => setGrantTarget(u)} data-testid={`grant-${u.id}`}>
                <Gift className="w-3.5 h-3.5" /> Grant
              </Button>
            )}
            {u.is_comp && (
              <Button size="sm" variant="ghost" onClick={() => revokeComp(u.id, u.email)} className="text-fuchsia-700" data-testid={`revoke-comp-${u.id}`}>
                Revoke comp
              </Button>
            )}
            {status === "active" && !u.is_admin && (
              <Button size="sm" variant="ghost" onClick={() => setStatus(u.id, "revoked")} data-testid={`revoke-${u.id}`}>
                Revoke access
              </Button>
            )}
            {status === "pending" && (
              <Button size="sm" variant="ghost" onClick={() => setStatus(u.id, "rejected")} className="text-red-700" data-testid={`reject-${u.id}`}>
                Reject
              </Button>
            )}
            {!u.is_admin && (
              <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id, u.email)} className="text-slate-500" data-testid={`delete-${u.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
          <div className="md:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="ghost"><MoreHorizontal className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {status !== "active" && (
                  <DropdownMenuItem onClick={() => setStatus(u.id, "active")}>Approve</DropdownMenuItem>
                )}
                {status === "active" && (
                  <DropdownMenuItem onClick={() => setGrantTarget(u)}>Grant free plan</DropdownMenuItem>
                )}
                {u.is_comp && (
                  <DropdownMenuItem onClick={() => revokeComp(u.id, u.email)} className="text-fuchsia-700">Revoke comp</DropdownMenuItem>
                )}
                {status === "active" && !u.is_admin && (
                  <DropdownMenuItem onClick={() => setStatus(u.id, "revoked")}>Revoke access</DropdownMenuItem>
                )}
                {status === "pending" && (
                  <DropdownMenuItem onClick={() => setStatus(u.id, "rejected")} className="text-red-700">Reject</DropdownMenuItem>
                )}
                {!u.is_admin && (
                  <DropdownMenuItem onClick={() => deleteUser(u.id, u.email)} className="text-red-700">Delete</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Admin</div>
          <h1 className="mt-1 font-display font-black tracking-tight text-2xl sm:text-4xl lg:text-5xl text-slate-900">Users</h1>
          <p className="mt-1.5 text-sm sm:text-base text-slate-600">Approve, revoke, grant free plans, or manage discount codes.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/codes"><Button variant="outline" size="sm" data-testid="nav-admin-codes"><Tag className="w-4 h-4" /> <span className="hidden sm:inline">Codes</span></Button></Link>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-users"><RefreshCcw className="w-4 h-4" /> <span className="hidden sm:inline">Refresh</span></Button>
        </div>
      </header>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
          <StatCard label="Users" value={stats.total_users} testId="stat-total" />
          <StatCard label="Pending" value={stats.pending_approval} testId="stat-pending" accent="amber" />
          <StatCard label="Paid subs" value={stats.paid_active_subscriptions} testId="stat-paid" accent="emerald" />
          <StatCard label="Trials" value={stats.by_tier?.trial || 0} testId="stat-trials" accent="blue" />
          <StatCard label="Comps" value={stats.by_tier?.comp || 0} testId="stat-comps" accent="fuchsia" />
          <StatCard label="Posts today" value={stats.attempts_success_today} testId="stat-posts-today" />
        </div>
      )}

      {pending.length > 0 && (
        <Card className="border-zinc-200 overflow-hidden" data-testid="pending-users-card">
          <div className="px-4 py-3 border-b border-zinc-200 bg-amber-50 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-700" />
            <div className="text-sm font-medium text-amber-900">{pending.length} pending request{pending.length === 1 ? "" : "s"}</div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.map(renderRow)}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Card className="border-zinc-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">All users</div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">Loading…</TableCell></TableRow>
              ) : others.length === 0 && pending.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No users yet.</TableCell></TableRow>
              ) : (
                others.map(renderRow)
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <GrantPlanDialog target={grantTarget} onClose={() => setGrantTarget(null)} onDone={load} />
    </div>
  );
}

function StatCard({ label, value, testId, accent }) {
  const accentClass = {
    amber: "text-amber-700",
    emerald: "text-emerald-700",
    blue: "text-blue-700",
    fuchsia: "text-fuchsia-700",
  }[accent] || "text-slate-900";
  return (
    <Card className="p-4 border-zinc-200" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-black tabular-nums ${accentClass}`}>{value}</div>
    </Card>
  );
}

function GrantPlanDialog({ target, onClose, onDone }) {
  const [plan, setPlan] = useState("pro");
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) { setPlan("pro"); setDays(30); setReason(""); }
  }, [target]);

  if (!target) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/users/${target.id}/grant-plan`, {
        plan_type: plan,
        duration_days: Number(days),
        reason: reason || null,
      });
      toast.success(`Granted ${plan} to ${data.email} until ${new Date(data.expires_at).toLocaleDateString()}`);
      onClose();
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="grant-plan-dialog">
        <DialogHeader>
          <DialogTitle>Grant free plan</DialogTitle>
          <p className="text-sm text-slate-600 mt-1">
            Comp <span className="font-medium">{target.email}</span> with a paid tier — no Stripe charge, no MRR pollution.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="grant-plan">Plan</Label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger id="grant-plan" data-testid="grant-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="basic">Basic — 5v + 5c / day</SelectItem>
                <SelectItem value="pro">Pro — 20v + 20c / day</SelectItem>
                <SelectItem value="ultra">Ultra — 50v + 50c / day</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="grant-days">Duration (days)</Label>
            <Input id="grant-days" type="number" min="1" max="3650" value={days} onChange={(e) => setDays(e.target.value)} data-testid="grant-days-input" />
          </div>
          <div>
            <Label htmlFor="grant-reason">Reason (optional)</Label>
            <Input id="grant-reason" placeholder="e.g. influencer partnership" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="grant-reason-input" maxLength={200} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="grant-plan-confirm-btn">
              {saving ? "Granting…" : "Grant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
